import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Callout,
  EmptyState,
  Paginator,
  Spinner,
  Tag,
  TextField,
  TreeTable,
  type TreeNode,
  type TreeTableColumn,
} from '@humanforce/mango-ui-react';
import type {
  PayRunPopulationCheckResult,
  PayRunPopulationPage,
  PayRunPopulationRefreshAccepted,
  PopulationLoadStatus,
} from './payRunPopulationApiTypes';
import type { PayRunCalculationStatus } from './payRunsApiTypes';
import {
  checkPopulationChanges,
  getPopulation,
  refreshPopulation,
} from './payRunPopulationHttpClient';

interface PayRunEmployeesTabProps {
  payId: string;
  workflowStatus: string;
  calculationStatus?: PayRunCalculationStatus;
  populationStatus: PopulationLoadStatus | null | undefined;
  canRefreshPopulation: boolean;
  onRefreshAccepted?: (accepted: PayRunPopulationRefreshAccepted) => void;
}

const pageSize = 20;
const unlockedStatuses = new Set(['open', 'payroll_active']);
const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const number = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 });

const words = (value: string): string => value
  .split('_')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const lockedStatusLabel = (value: string): string =>
  value === 'ready_to_pay' ? 'Ready to Pay' : words(value);

export const PayRunEmployeesTab = ({
  payId,
  workflowStatus,
  calculationStatus = 'not_ready',
  populationStatus,
  canRefreshPopulation,
  onRefreshAccepted,
}: PayRunEmployeesTabProps) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [result, setResult] = useState<PayRunPopulationPage | null>(null);
  const [change, setChange] = useState<PayRunPopulationCheckResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({});

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      setResult(await getPopulation(payId, { page, pageSize, search }, signal));
    } catch (loadError) {
      if (!signal?.aborted) {
        setError(loadError instanceof Error ? loadError.message : 'Employees could not be loaded.');
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, payId, search]);

  const check = useCallback(async (signal?: AbortSignal) => {
    setChecking(true);
    try {
      setChange(await checkPopulationChanges(payId, signal));
    } catch {
      if (!signal?.aborted) setChange(null);
    } finally {
      if (!signal?.aborted) setChecking(false);
    }
  }, [payId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, populationStatus]);

  useEffect(() => {
    const controller = new AbortController();
    void check(controller.signal);
    return () => controller.abort();
  }, [check, populationStatus]);

  const columns = useMemo<TreeTableColumn[]>(() => {
    const calculated = calculationStatus === 'complete';
    const calculatedMoney = (value: number) => calculated ? currency.format(value) : '—';
    const data = (node: TreeNode) => node.data as Record<string, unknown>;
    return [
    {
      field: 'displayName',
      header: 'Employee',
      body: (node) => data(node).kind === 'transaction'
        ? <span className="mgo-stack mgo-gap-2xs"><strong>{words(String(data(node).factTypeCode))}</strong><span className="mgo-text-xs mgo-color-muted">{String(data(node).sourceRecordId || data(node).source)}</span></span>
        : (
        <span className="mgo-stack mgo-gap-2xs">
          <strong>{String(data(node).displayName)}</strong>
          <span className="mgo-text-xs mgo-color-muted">{String(data(node).employeeNumber)}</span>
        </span>
      ),
    },
    { field: 'roleTitle', header: 'Role', body: (node) => String(data(node).roleTitle || '—') },
    { field: 'units', header: 'Units', body: (node) => data(node).units == null ? '—' : number.format(Number(data(node).units)) },
    { field: 'hourlyUnits', header: 'Hrly units', body: (node) => data(node).hourlyUnits == null ? '—' : number.format(Number(data(node).hourlyUnits)) },
    { field: 'gross', header: 'Gross', body: (node) => data(node).gross == null ? '—' : calculatedMoney(Number(data(node).gross)) },
    { field: 'taxable', header: 'Taxable', body: (node) => data(node).taxable == null ? '—' : calculatedMoney(Number(data(node).taxable)) },
    { field: 'tax', header: 'Tax', body: (node) => data(node).tax == null ? '—' : calculatedMoney(Number(data(node).tax)) },
    { field: 'stsl', header: 'STSL', body: () => '—' },
    { field: 'btd', header: 'BTD', body: () => '—' },
    { field: 'super', header: 'Super', body: () => '—' },
    { field: 'atd', header: 'ATD', body: () => '—' },
    { field: 'netPay', header: 'Net pay', body: (node) => data(node).netPay == null ? '—' : calculatedMoney(Number(data(node).netPay)) },
    {
      field: 'exceptionSeverity',
      header: 'Excn',
      body: (node) => data(node).kind === 'transaction' ? '' : data(node).exceptionSeverity
        ? <Tag label={words(String(data(node).exceptionSeverity))} tone={data(node).exceptionSeverity === 'blocking' ? 'critical' : 'caution'} />
        : <Tag label="Clear" tone="positive" />,
    },
    ];
  }, [calculationStatus]);

  const treeNodes = useMemo<TreeNode[]>(() => (result?.items ?? []).map((employee) => ({
    key: `employee-${employee.personId}-${employee.employmentRecordId}`,
    data: { ...employee, kind: 'employee' },
    children: (employee.transactions ?? []).map((transaction) => ({
      key: `transaction-${employee.employmentRecordId}-${transaction.id}`,
      leaf: true,
      data: {
        kind: 'transaction',
        displayName: transaction.factTypeCode,
        factTypeCode: transaction.factTypeCode,
        source: transaction.source,
        sourceRecordId: transaction.sourceRecordId,
        roleTitle: transaction.effectiveDate,
        units: transaction.factTypeCode === 'normal_hours_earning' || transaction.factTypeCode === 'transaction_input' ? transaction.quantity : null,
        hourlyUnits: transaction.factTypeCode === 'normal_hours_earning' || transaction.factTypeCode === 'transaction_input' ? transaction.quantity : null,
        gross: transaction.factTypeCode === 'gross_pay' || transaction.factTypeCode === 'transaction_input' ? transaction.amount : null,
        taxable: transaction.factTypeCode === 'taxable_gross' ? transaction.amount : null,
        tax: transaction.factTypeCode === 'payg_withholding' ? transaction.amount : null,
        netPay: transaction.factTypeCode === 'net_pay' ? transaction.amount : null,
      },
    })),
  })), [result]);

  const apply = async () => {
    setApplying(true);
    setActionError(null);
    try {
      const accepted = await refreshPopulation(payId);
      onRefreshAccepted?.(accepted);
    } catch (refreshError) {
      setActionError(refreshError instanceof Error ? refreshError.message : 'Employee changes could not be applied.');
    } finally {
      setApplying(false);
    }
  };

  const unlocked = unlockedStatuses.has(workflowStatus);
  const hasActivePopulation = result?.revisionNumber != null;
  const showAction = canRefreshPopulation && unlocked && !checking && !applying
    && populationStatus !== 'loading'
    && (!hasActivePopulation || change?.changeAvailable === true);

  return (
    <section aria-label="Employee population" className="mgo-stack mgo-gap-l" aria-busy={loading || applying}>
      <div className="mgo-split mgo-align-end mgo-gap-m">
        <TextField
          label="Search employees"
          placeholder="Search employees, role or employee number"
          leadingIcon="search"
          clearable
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(1); }}
          onClear={() => { setSearch(''); setPage(1); }}
        />
        {result && <span className="mgo-text-s mgo-color-muted">{result.employeeCount} employees</span>}
      </div>

      {populationStatus === 'loading' && (
        <Callout tone="info" title="Applying employee changes">
          <span className="mgo-cluster mgo-gap-s"><Spinner /> A refreshed employee snapshot is being prepared.</span>
        </Callout>
      )}

      {change?.changeAvailable && (
        <Callout
          tone="caution"
          title="Employee changes available"
          action={showAction
            ? <Button label="Apply employee changes" icon="refresh" loading={applying} onClick={() => void apply()} />
            : undefined}
        >
          {unlocked
            ? canRefreshPopulation
              ? `${change.uniquePersonCount} employees are available in the latest HR snapshot.`
              : `${change.uniquePersonCount} employees are available; a Payroll Administrator can apply them.`
            : `Changes cannot be applied because this Pay Run is locked at ${lockedStatusLabel(workflowStatus)}.`}
        </Callout>
      )}

      {actionError && <Callout tone="critical" title="Employee changes not applied">{actionError}</Callout>}
      {error && (
        <Callout
          tone="critical"
          title="Employees unavailable"
          action={<Button label="Retry" variant="secondary" size="small" onClick={() => void load()} />}
        >
          {error}
        </Callout>
      )}

      {loading && !error ? (
        <div className="mgo-cluster mgo-justify-center mgo-gap-s" style={{ minHeight: '12rem' }}>
          <Spinner /> <span>Loading employees…</span>
        </div>
      ) : !error && !hasActivePopulation ? (
        <EmptyState
          variant="no-data"
          title="No employee population loaded"
          description={canRefreshPopulation
            ? 'Choose the employee and Timesheet sources for this Pay Run.'
            : 'A Payroll Administrator must load the employee population for this Pay Run.'}
        />
      ) : !error && result && result.items.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No employees match your search"
          description="Change or clear the current search."
          actions={[<Button key="clear" label="Clear search" variant="secondary" onClick={() => { setSearch(''); setPage(1); }} />]}
        />
      ) : !error && result ? (
        <div style={{ overflowX: 'auto' }}>
          <TreeTable
            value={treeNodes}
            columns={columns}
            expandedKeys={expandedKeys}
            onToggle={setExpandedKeys}
            scrollable
            emptyMessage="No employees."
          />
        </div>
      ) : null}

      {!loading && !error && result && result.pagination.totalPages > 1 && <Paginator first={(page - 1) * pageSize}
        itemsPerPage={pageSize} totalRecords={result.pagination.totalItems}
        onPageChange={(event) => setPage(event.page + 1)} />}
    </section>
  );
};
